import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import Typography from '@src/new-components/Typography';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { Box, Fade, MenuItem, Paper, Popper } from '@mui/material';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import { useDispatch } from 'react-redux';
import actions from '@src/redux/actions';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import { VOICE_PROFILE_PAGE_STATUS } from '@src/constants/voiceCloning';
import {
  FAVOURITE_VOICES_ACTION,
  FAVOURITE_VOICES_LIMIT,
} from '@src/constants/voiceLibrary';
import routes from '@src/constants/route';
import { VOICE_CODE } from '@src/constants/voice';
import { StyledNewVoiceAction } from './index.style';

const NewVoiceAction = ({
  onUseVoice,
  isCard,
  isHovered,
  profilePage,
  voiceCode,
  hiddenCopyLink,
  onUpdateFavouriteVoices,
  onFavoriteToggleRequest,
  favouriteVoiceCodes = [],
  isDubbing,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [anchorBlockOption, setAnchorBlockOption] = useState(null);
  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [queueFavouriteVoices, setQueueFavouriteVoices] = useState([]);
  const refClickOutSide = useClickOutside(() => {
    setOpenPopperBlockOption(false);
  });

  const { status, slug } = profilePage || {};

  const notify = (message, severity) => {
    dispatch(actions.noti.push({ severity, message }));
  };

  const handleClickFavorite = (e) => {
    e.stopPropagation();
    const result = onFavoriteToggleRequest(voiceCode);

    if (!result.allowed) {
      if (result.reason === 'limit') {
        notify(
          t('maxFavouriteVoices', {
            numFavouriteVoices: FAVOURITE_VOICES_LIMIT,
          }),
          'warning',
        );
      }
      return;
    }
    setIsFavorite(!isFavorite);
    setQueueFavouriteVoices([
      ...queueFavouriteVoices,
      { voiceCode, isFavorite },
    ]);
  };

  const handleClickCopy = (content, message) => {
    if (content) {
      navigator.clipboard.writeText(content);
      notify(message, 'success');
    }
  };

  const getCopyLink = () => {
    if (status === VOICE_PROFILE_PAGE_STATUS.PUBLIC) {
      const slugProfile = slug ?? voiceCode;
      const linkProfile = `${LANDING_PAGE_URL}/voices/${slugProfile}`;
      return linkProfile;
    }
    const linkVoiceLibrary = `${CRM_URL}${routes.VOICE_LIBRARY}?${VOICE_CODE}=${voiceCode}`;
    return linkVoiceLibrary;
  };

  const processingQueueFavouriteVoices = async () => {
    if (queueFavouriteVoices.length === 0 || isProcessing) return;
    setIsProcessing(true);
    const task = queueFavouriteVoices[0];
    try {
      await onUpdateFavouriteVoices({
        voiceCode: task.voiceCode,
        action: task.isFavorite
          ? FAVOURITE_VOICES_ACTION.REMOVE
          : FAVOURITE_VOICES_ACTION.ADD,
      });
    } catch (error) {
      setIsFavorite(!isFavorite);
    }
    setQueueFavouriteVoices((prev) => prev.slice(1));
    setIsProcessing(false);
  };

  const renderButtonUseVoice = () => {
    if (!isHovered) return null;
    return (
      <Button
        size="small"
        variant="outlined"
        onClick={onUseVoice}
        customStyle={{
          marginRight: '0px',
          '& .MuiButton-startIcon': {
            margin: '0px',
          },
          borderColor: COLOR.indigo[16],
          height: isCard ? '32px' : '40px',
          minWidth: '32px',
          padding: isCard ? '8px 16px' : '8px 24px',
          borderRadius: '8px',
          '&:hover': {
            borderColor: COLOR.indigo[16],
          },
        }}
      >
        <Typography variant="body-14-semibold" color={COLOR.indigo.default}>
          {t('voiceUse')}
        </Typography>
      </Button>
    );
  };

  const renderPopoverMenuItem = ({ onClick, label, icon }) => (
    <MenuItem
      key="recently"
      className="option_filter"
      style={{
        color: '#1E164E',
        fontSize: '14px',
        height: '40px',
        boxSizing: 'border-box',
        padding: '10px 16px 10px 16px',
        borderRadius: '8px',
        fontWeight: 400,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#1E164E14';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '';
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
    >
      {icon}
      {label}
    </MenuItem>
  );

  useEffect(() => {
    if (queueFavouriteVoices.length > 0) return;
    setIsFavorite(favouriteVoiceCodes.includes(voiceCode));
  }, [favouriteVoiceCodes]);

  useEffect(() => {
    processingQueueFavouriteVoices();
  }, [isProcessing, queueFavouriteVoices]);

  return (
    <StyledNewVoiceAction isCard={isCard}>
      {!isCard && renderButtonUseVoice()}
      <Box className="new-voice-action-container">
        {/* Button favorite */}
        {!isDubbing && (
          <Button
            size="small"
            variant="transparent"
            customStyle={{
              marginRight: '0px',
              '& .MuiButton-startIcon': {
                margin: '0px',
              },
              border: 'none',
              height: isCard ? '32px' : '40px',
              width: isCard ? '32px' : '40px',
              minWidth: '32px',
              padding: '8px',
              borderRadius: '8px',
            }}
            onClick={handleClickFavorite}
            startIcon={
              isFavorite ? (
                <FavoriteRoundedIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: COLOR.indigo.default,
                  }}
                />
              ) : (
                <FavoriteBorderRoundedIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: COLOR.indigo[80],
                  }}
                />
              )
            }
          />
        )}
        {/* Button more */}
        <div ref={refClickOutSide}>
          <Button
            size="small"
            variant="transparent"
            customStyle={{
              marginRight: '0px',
              '& .MuiButton-startIcon': {
                margin: '0px',
              },
              border: 'none',
              height: isCard ? '32px' : '40px',
              width: isCard ? '32px' : '40px',
              minWidth: '32px',
              padding: '8px',
              borderRadius: '8px',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpenPopperBlockOption(!openPopperBlockOption);
              setAnchorBlockOption(e.currentTarget);
            }}
            startIcon={
              <MoreVertRoundedIcon
                sx={{ width: '24px', height: '24px', color: COLOR.indigo[80] }}
              />
            }
          />
        </div>
        <Popper
          transition
          style={{
            zIndex: 9999,
            color: '#1E164E',
          }}
          anchorEl={anchorBlockOption}
          open={openPopperBlockOption}
          placement="bottom-end"
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade
              style={{
                padding: '8px',
                boxShadow: '0px 0px 20px 2px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
              {...TransitionProps}
              timeout={350}
            >
              <Paper>
                {renderPopoverMenuItem({
                  onClick: (e) => {
                    e.stopPropagation();
                    handleClickCopy(voiceCode, t('copiedVoiceCode'));
                    setAnchorBlockOption(null);
                    setOpenPopperBlockOption(false);
                  },
                  label: t('copyVoiceCode'),
                  icon: <ContentCopyRoundedIcon />,
                })}
                {!hiddenCopyLink &&
                  renderPopoverMenuItem({
                    onClick: (e) => {
                      e.stopPropagation();
                      handleClickCopy(getCopyLink(), t('copiedLinkVoice'));
                      setAnchorBlockOption(null);
                      setOpenPopperBlockOption(false);
                    },
                    label: t('copyLinkVoice'),
                    icon: <InsertLinkRoundedIcon />,
                  })}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
      {isCard && renderButtonUseVoice()}
    </StyledNewVoiceAction>
  );
};

export default NewVoiceAction;
